import { useEffect, useState } from 'react';
import {
  delDownLoad,
  getDownLoadItems,
  createDownLoad,
  updateDownLoad,
} from '../../../api';
import { messageError, messageSuccess } from '../../../components/message';
import warnImage from '../../../assets/images/warn-yellow.png';
import {
  CommonStatusStep,
  IDownLoadMenu,
  IDownLoadType,
} from '../../../model/api-modules';
import { DownLoadDrawer } from './download-drawer';
import { DownloadTable } from './lib/download-table';
import { LabeledValue } from 'antd/lib/select';
import { getStudioOptions } from '../../utils/admin';
import getFormatMessage from '../../../locales/get-format-message';
import styles from './index.module.scss';

import { DownloadWhiteList } from './dialog/white-list';
import { wrapRequestArea } from '../../../lib/http/lib';
import {
  getFirstManageSite,
  getNeedManageSites,
  getNeedManageSitesWithSync,
  getNeedSynSiteOptions,
} from '../../../lib/multi-site';
import {
  handleMultiSiteSubmitRes,
  multiSiteSubmitter,
} from './lib/multi-site-helper';
import {
  CommonAddButton,
  CommonButtonGroup,
  MainButton,
} from '../../../components/button/main';
import { omit } from 'lodash';
import { ModalWrap } from '../../../components/modal-wrap';
import { showSyncModal } from '../lib/sync-helper';
import { shortHash10 } from '../lib/hash';

export type IStudioOptions = Record<string, LabeledValue[]>;

export type ISiteIDownLoadMenu = Record<string, IDownLoadMenu[]>;

export const DownLoadItems = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [downLoadList, setDownLoadList] = useState<IDownLoadMenu[]>([]);
  const [allSiteDownLoadList, setAllSiteDownLoadList] =
    useState<ISiteIDownLoadMenu>({});
  const [row, setRow] = useState<IDownLoadMenu | null>(null);
  const [editType, setEditType] = useState<IDownLoadType>();
  const [delRow, setDelRow] = useState<IDownLoadMenu | null>(null);
  const [showWhiteModel, setShowWhiteModel] = useState<boolean>(false);
  const [studioList, setStudioList] = useState<IStudioOptions>({});

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const getDownLoadList = async () => {
    setLoading(true);
    const allSiteList = await getAllSiteDownLoadList();
    const woaRes = allSiteList[getFirstManageSite()];
    if (woaRes) {
      setDownLoadList(woaRes);
    } else {
      messageError(getFormatMessage('download_list_fail'));
    }
    setLoading(false);
  };

  const getAllSiteDownLoadList = async () => {
    const allSites = getNeedManageSitesWithSync();
    const currentList: ISiteIDownLoadMenu = {};

    await Promise.all(
      allSites.map(async (siteName) => {
        const res = await wrapRequestArea(getDownLoadItems, siteName);
        currentList[siteName] = res || [];
      })
    );
    setAllSiteDownLoadList({ ...currentList });
    return currentList;
  };

  const onDel = (record: IDownLoadMenu) => {
    setDelRow(record);
  };
  const onView = (record: IDownLoadMenu) => {
    setRow(record);
    setEditType(IDownLoadType.View);
  };
  const onEdit = (record: IDownLoadMenu) => {
    setRow(record);
    setEditType(IDownLoadType.Edit);
  };
  const onWhite = (record: IDownLoadMenu) => {
    setRow(record);
    setShowWhiteModel(true);
  };
  const onAdd = () => {
    setEditType(IDownLoadType.Create);
  };
  const onClose = (success?: boolean) => {
    setRow(null);
    setDelRow(null);
    setEditType(undefined);
    if (success) {
      init();
    }
  };
  const onCloseWhite = (success?: boolean) => {
    setRow(null);
    if (success) {
      init();
    }
    setShowWhiteModel(false);
  };

  const initStudioOptions = async () => {
    if (!showWhiteModel) {
      return;
    }
    const allSiteStudios: Record<string, LabeledValue[]> = {};
    await Promise.all(
      getNeedManageSites().map(async (site) => {
        allSiteStudios[site] = await getStudioOptions(site);
      })
    );
    setStudioList(allSiteStudios);
  };

  useEffect(() => {
    initStudioOptions();
  }, [showWhiteModel]);

  const renderWhiteModal = () => {
    if (showWhiteModel && row) {
      return (
        <DownloadWhiteList
          info={row}
          onSave={() => {
            onCloseWhite(true);
          }}
          onClose={() => onCloseWhite(false)}
          studioOptions={studioList}
          allSiteDownLoadList={allSiteDownLoadList}
        />
      );
    }
    return null;
  };
  const confirmDel = async () => {
    if (delRow) {
      const res = await multiSiteSubmitter(
        (matchRow: IDownLoadMenu) => delDownLoad(matchRow.id),
        (site) =>
          allSiteDownLoadList[site]?.find((item) => item.Name === delRow.Name)
      );
      return handleMultiSiteSubmitRes(res, {
        onSuccess: () => {
          getDownLoadList();
          onClose(true);
        },
        successMessage: getFormatMessage('del_success'),
        errorMessage: getFormatMessage('error_delete'),
      });
    }
    messageError(getFormatMessage('error_delete'));
    return false;
  };

  const renderDelModal = () => {
    if (delRow) {
      const name = delRow.Name;
      return (
        <ModalWrap
          onOk={confirmDel}
          onClose={() => onClose(false)}
          cancelText={getFormatMessage('cancel')}
          oKText={getFormatMessage('确认')}
          width="480px"
          title={
            <p className="fail-title">
              <img
                src={warnImage}
                style={{ marginRight: '10px', marginTop: '-2px' }}
              />
              <span>{getFormatMessage('删除下载物')}</span>
            </p>
          }
          className="delete-modal"
        >
          <div>{`是否确认删除【${name}】的配置信息?`}</div>
        </ModalWrap>
      );
    }
    return null;
  };
  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    await getDownLoadList();
  };

  const renderDownLoadDrawer = () => {
    if (editType) {
      const editRow = [IDownLoadType.Edit, IDownLoadType.View].includes(
        editType
      )
        ? row
        : null;
      return (
        <DownLoadDrawer
          allSiteDownLoadList={allSiteDownLoadList}
          row={editRow}
          onClose={onClose}
          type={editType}
        />
      );
    }
    return null;
  };

  const syncToSingapore = () => {
    showSyncModal(async (syncSite) => {
      const singaporeDownloadItems = allSiteDownLoadList[syncSite];

      if (!singaporeDownloadItems) {
        messageError('海外站点获取服务出错');
        return false;
      }
      const selectedDownloadItems = downLoadList.filter((item) =>
        selectedRowKeys.includes(item.id)
      );

      const needSyncPrams = selectedDownloadItems.map((downloadItem) => {
        const singaporeDownItem = singaporeDownloadItems.find(
          (item) => item.Tag === downloadItem.Tag
        );
        const updateInfo = downloadItem;

        if (!singaporeDownItem) {
          return { ...updateInfo, id: 0, Status: CommonStatusStep.Pending };
        }
        return {
          ...singaporeDownItem,
          ...updateInfo,
          Status: singaporeDownItem.Status,
          id: singaporeDownItem.id,
        };
      });

      const submitRes = await Promise.all(
        needSyncPrams.map(async (params) => {
          const omitParams: any = omit(params, [
            'CreatorID',
            'CreateTime',
            'CreatorUserID',
            'ModifierID',
            'ModifierUserID',
            'ModifyTime',
            'VersionItem',
            'StudioWhitelistArray',
            'Type',
            'id',
            'DataSource',
            'DataSyncID',
            'ParentID',
          ]);
          const itemID = params.id;
          omitParams.StudioWhitelist = params.StudioWhitelistArray;
          if (itemID) {
            const updateParams: any = omit(omitParams, ['Tag', 'ParentTag']);
            return wrapRequestArea(
              () => updateDownLoad(itemID, updateParams),
              syncSite
            );
          }
          const dataSyncID = await shortHash10(omitParams.Name);
          return wrapRequestArea(
            () =>
              createDownLoad({
                ...omitParams,
                Type: params.Type,
                DataSyncID: dataSyncID,
                DataSource: 'support',
              }),
            syncSite
          );
        })
      );
      if (submitRes.some((item) => !item)) {
        messageError(getFormatMessage('failed_submit'));
        return false;
      }
      messageSuccess(getFormatMessage('successfully'));
      return true;
    });
  };

  return (
    <>
      <div className={styles.pageActions}>
        <CommonButtonGroup>
          <CommonAddButton onClick={onAdd}>
            {getFormatMessage('create_download')}
          </CommonAddButton>
          {getNeedSynSiteOptions().length > 0 && (
            <MainButton
              onClick={syncToSingapore}
              style={{ marginRight: 10 }}
              disabled={selectedRowKeys.length === 0}
            >
              {getFormatMessage('同步至其它站点')}
            </MainButton>
          )}
        </CommonButtonGroup>
      </div>
      <DownloadTable
        onChange={init}
        loading={loading}
        downLoadList={downLoadList || []}
        allSiteList={allSiteDownLoadList}
        onEdit={onEdit}
        onView={onView}
        onWhite={onWhite}
        onDel={onDel}
        selectedRowKeys={selectedRowKeys}
        onSelectChange={(keys) => {
          setSelectedRowKeys(keys as number[]);
        }}
      />
      {renderDownLoadDrawer()}
      {renderDelModal()}
      {renderWhiteModal()}
    </>
  );
};
